import { useSeoMeta } from '@unhead/react';
import { Server, Database, Cpu } from 'lucide-react';
import { AppLayout } from '@/components/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { DataStatus, timeAgo } from '@/components/DataStatus';
import { useLiquidityData } from '@/hooks/useLiquidityData';
import { INDICATORS } from '@/lib/indicators';
import { formatValue } from '@/lib/format';
import { cn } from '@/lib/utils';

const Admin = () => {
  useSeoMeta({
    title: 'Sources & Data Health — BCH Radar',
    description: 'Where BCH Radar\'s data comes from, and how fresh it is.',
  });

  const { data, isLoading } = useLiquidityData();

  const obsById = new Map(
    (data?.snapshot.observations ?? []).map((o) => [o.indicatorId, o]),
  );
  const missing = (data?.snapshot.observations ?? []).filter(
    (o) => o.quality === 'unavailable',
  );
  const stale = (data?.snapshot.observations ?? []).filter(
    (o) => o.quality === 'stale',
  );
  const fresh = (data?.snapshot.observations ?? []).filter(
    (o) => o.quality === 'fresh',
  );

  return (
    <AppLayout>
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-chart-2/15 text-chart-2">
          <Server className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Sources</h1>
          <p className="text-sm text-muted-foreground">
            Where the data comes from and how fresh it is.
          </p>
        </div>
      </div>

      {isLoading || !data ? (
        <div className="mt-5 space-y-3">
          <Skeleton className="h-24 w-full rounded-xl" />
          <Skeleton className="h-64 w-full rounded-xl" />
        </div>
      ) : (
        <div className="mt-5 space-y-6">
          {/* Health summary */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <HealthStat label="Live feeds" value={data.liveCount} tone="primary" />
            <HealthStat label="Fresh" value={fresh.length} tone="primary" />
            <HealthStat label="Stale" value={stale.length} tone="warn" />
            <HealthStat label="Missing" value={missing.length} tone="danger" />
          </div>

          <Card>
            <CardContent className="flex items-center justify-between p-4 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Cpu className="h-4 w-4" /> Last successful update
              </div>
              <span className="font-mono">{timeAgo(data.lastUpdated)}</span>
            </CardContent>
          </Card>

          {/* Data status — user-facing, no implementation details */}
          <Card
            className={cn(
              'border-primary/30 bg-primary/5',
              !data.serverSourced && 'border-yellow-500/30 bg-yellow-500/5',
            )}
          >
            <CardContent className="flex items-center gap-3 p-4 text-sm">
              <Database
                className={cn(
                  'h-4 w-4 shrink-0',
                  data.serverSourced ? 'text-primary' : 'text-yellow-400',
                )}
              />
              <div>
                <p className="font-medium">
                  {data.serverSourced ? (
                    <span className="text-primary">Live data connected</span>
                  ) : (
                    <span className="text-yellow-400">Limited data</span>
                  )}
                </p>
                <p className="text-muted-foreground">
                  {data.serverSourced
                    ? 'Macro, rates, currency, and crypto indicators are updating from primary sources. Scores are recalculated as data changes.'
                    : 'Live market data is available; some macro indicators are temporarily using baseline values. Signal confidence is reduced accordingly.'}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Feed table */}
          <div>
            <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              Feed health
            </h2>
            <Card>
              <CardContent className="divide-y divide-border/60 p-0">
                {INDICATORS.map((def) => {
                  const obs = obsById.get(def.id);
                  return (
                    <div
                      key={def.id}
                      className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm"
                    >
                      <div className="flex min-w-0 items-center gap-2.5">
                        <DataStatus
                          quality={obs?.quality ?? 'unavailable'}
                          showLabel={false}
                        />
                        <span className="truncate">{def.short}</span>
                      </div>
                      <div className="flex shrink-0 items-center gap-4">
                        <span className="font-mono text-xs">
                          {formatValue(def, obs?.value ?? null)}
                        </span>
                        <span className="w-16 text-right text-xs text-muted-foreground">
                          {obs?.timestamp ? timeAgo(obs.timestamp) : '—'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </div>

        </div>
      )}
    </AppLayout>
  );
};

function HealthStat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'primary' | 'warn' | 'danger';
}) {
  return (
    <Card>
      <CardContent className="p-3 text-center">
        <p
          className={cn(
            'font-mono text-2xl font-bold tabular-nums',
            tone === 'primary' && 'text-primary',
            tone === 'warn' && 'text-yellow-400',
            tone === 'danger' && value > 0 ? 'text-red-400' : tone === 'danger' && 'text-muted-foreground',
          )}
        >
          {value}
        </p>
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
          {label}
        </p>
      </CardContent>
    </Card>
  );
}

export default Admin;
